import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { freezones as mockFreezones, businessActivities } from '../mock';
import { getFreezones, costEstimate } from '../api';
import { Calculator as CalcIcon, Building2, Globe, ArrowRight } from 'lucide-react';

const Calculator = () => {
  const { t, pick, lang, dir } = useLang();
  const [type, setType] = useState('freezone');
  const [zone, setZone] = useState('ifza');
  const [activity, setActivity] = useState('trading');
  const [visas, setVisas] = useState([2]);
  const [office, setOffice] = useState('flexi');
  const [shareholders, setShareholders] = useState([1]);
  const [freezones, setFreezones] = useState(mockFreezones);
  const [remoteBreakdown, setRemoteBreakdown] = useState(null);

  useEffect(() => {
    getFreezones().then((d) => d && d.length && setFreezones(d.map(z => ({ ...z, minCost: z.min_cost, id: z.code })))).catch(() => {});
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    costEstimate({ type, zone_code: zone, visas: visas[0], office, shareholders: shareholders[0] })
      .then(setRemoteBreakdown)
      .catch(() => setRemoteBreakdown(null));
    return () => controller.abort();
  }, [type, zone, visas, office, shareholders]);

  const breakdown = useMemo(() => {
    if (remoteBreakdown) return remoteBreakdown;
    const zoneObj = freezones.find((z) => (z.code || z.id) === zone);
    const licenseFee = type === 'mainland' ? 18500 : (zoneObj?.minCost || zoneObj?.min_cost || 6000);
    const officeCost = office === 'flexi' ? 4500 : office === 'private' ? 12000 : 28000;
    const visaCost = visas[0] * 3200;
    const admin = 4500 + (shareholders[0] - 1) * 800;
    const taxReg = type === 'mainland' ? 2500 : 1500;
    return {
      license: licenseFee,
      office: officeCost,
      visa: visaCost,
      admin,
      tax: taxReg,
      total: licenseFee + officeCost + visaCost + admin + taxReg
    };
  }, [type, zone, office, visas, shareholders, remoteBreakdown, freezones]);

  return (
    <div className="pt-28 pb-16 min-h-screen" dir={dir}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#2563eb]/30 bg-[#2563eb]/5 mb-4">
            <CalcIcon className="w-3 h-3 text-[#3b82f6]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#3b82f6]">{t('nav.calculator')}</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-[#0a0a0a]">{t('calc.title')}</h1>
          <p className="mt-4 text-[#111827]/60 max-w-2xl mx-auto">{t('calc.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left form */}
          <div className="lg:col-span-3 glass-card rounded-2xl p-8 space-y-8">
            <div>
              <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-3 block">{t('calc.jurisdiction')}</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'mainland', icon: Building2, label: t('calc.mainland') },
                  { id: 'freezone', icon: Globe, label: t('calc.freezone') }
                ].map((o) => (
                  <button key={o.id} onClick={() => setType(o.id)} className={`p-4 rounded-lg border-2 transition-all flex items-center gap-3 ${type === o.id ? 'border-[#2563eb] bg-[#2563eb]/8' : 'border-[#2563eb]/15 hover:border-[#2563eb]/40'}`}>
                    <o.icon className={`w-5 h-5 ${type === o.id ? 'text-[#3b82f6]' : 'text-[#2563eb]/60'}`} />
                    <span className={`text-sm ${type === o.id ? 'text-[#3b82f6]' : 'text-[#111827]/70'}`}>{o.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {type === 'freezone' && (
              <div>
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-3 block">{t('calc.zone')}</Label>
                <Select value={zone} onValueChange={setZone}>
                  <SelectTrigger className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
                    {freezones.map((z) => (<SelectItem key={z.id} value={z.id} className="text-[#111827]">{pick(z.name)}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-3 block">{t('calc.activity')}</Label>
              <Select value={activity} onValueChange={setActivity}>
                <SelectTrigger className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#ffffff] border-[#2563eb]/25">
                  {businessActivities.map((a) => (<SelectItem key={a.id} value={a.id} className="text-[#111827]">{a[lang]}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest">{t('calc.visas')}</Label>
                <span className="font-display text-2xl gold-text">{visas[0]}</span>
              </div>
              <Slider value={visas} onValueChange={setVisas} min={1} max={15} step={1} className="[&_[role=slider]]:bg-[#2563eb] [&_[role=slider]]:border-[#2563eb]" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest">{t('calc.shareholders')}</Label>
                <span className="font-display text-2xl gold-text">{shareholders[0]}</span>
              </div>
              <Slider value={shareholders} onValueChange={setShareholders} min={1} max={10} step={1} />
            </div>

            <div>
              <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-3 block">{t('calc.office')}</Label>
              <RadioGroup value={office} onValueChange={setOffice} className="space-y-2">
                {[
                  { id: 'flexi', label: t('wizard.flexi'), cost: 4500 },
                  { id: 'private', label: t('wizard.private_office'), cost: 12000 },
                  { id: 'executive', label: t('wizard.executive'), cost: 28000 }
                ].map((o) => (
                  <div key={o.id} onClick={() => setOffice(o.id)} className={`flex items-center justify-between gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${office === o.id ? 'border-[#2563eb] bg-[#2563eb]/8' : 'border-[#2563eb]/15 hover:border-[#2563eb]/40'}`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value={o.id} className="border-[#2563eb]/40 text-[#2563eb]" />
                      <span className="text-sm text-[#111827]/85">{o.label}</span>
                    </div>
                    <span className="text-xs text-[#2563eb]">AED {o.cost.toLocaleString()}</span>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>

          {/* Right summary */}
          <div className="lg:col-span-2">
            <div className="glass-card rounded-2xl p-8 lg:sticky lg:top-24">
              <div className="text-xs uppercase tracking-widest text-[#2563eb] mb-2">{t('calc.total')}</div>
              <div className="font-display text-5xl gold-text mb-1">AED {breakdown.total.toLocaleString()}</div>
              <div className="text-xs text-[#111827]/50 mb-8">{lang === 'ar' ? 'تقدير تقريبي' : 'Approximate estimate'}</div>

              <div className="space-y-3 pt-6 border-t border-[#2563eb]/15">
                <div className="text-xs uppercase tracking-widest text-[#2563eb] mb-2">{t('calc.breakdown')}</div>
                {[
                  { label: t('calc.license'), val: breakdown.license },
                  { label: t('calc.office_fee'), val: breakdown.office },
                  { label: t('calc.visa_fee'), val: breakdown.visa },
                  { label: t('calc.admin'), val: breakdown.admin },
                  { label: t('calc.tax_reg'), val: breakdown.tax }
                ].map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-[#2563eb]/8 last:border-0">
                    <span className="text-[#111827]/70">{r.label}</span>
                    <span className="text-[#0a0a0a] font-medium">AED {r.val.toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <Link to="/Contact" className="block mt-8">
                <Button className="gold-button w-full rounded-md h-12 text-sm">{t('calc.get_quote')} <ArrowRight className="w-4 h-4 ms-2 rtl:hidden" /></Button>
              </Link>
              <Link to="/SetupWizard" className="block mt-3">
                <Button className="ghost-button w-full rounded-md h-12 text-sm bg-transparent">{t('cta.start_wizard')}</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
